import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import ListItem from '../ListItem';
import { colors } from '@/styles';

/**
 * ListContainer component
 *
 * @param {import('./ListContainer').ListContainerProps}
 */
const ListContainer = ({
  className,
  selection,
  enableUpwardMenus,
  listItems,
  layout,
  containerSize,
  enableCopyMenuItems,
  enableDeleteMenuItem,
  enableSendWithTemplate,
  enableVideoEdit,
  handleDropDown,
  onMenuOpen,
  onListItemSelect,
  enableMultiSelect,
  shouldShowMenu,
  videoTranscription,
  isRebrand,
  showReviewStatus,
}) => {
  const handleListItemSelect = useCallback(
    (id) => {
      let nextSelect = id;
      if (!enableMultiSelect) {
        nextSelect = selection === id ? '' : id;
      }
      onListItemSelect(nextSelect);
    },
    [onListItemSelect, selection, enableMultiSelect],
  );

  /**
   * Figure out if the menu item should open "up" or "down"
   *
   * @param {number} index
   */
  const shouldMenuOpenUp = useCallback(
    (index) => {
      if (!enableUpwardMenus) {
        return false;
      }

      // to determine if a list item is within the N positions of the end of the list
      // (compact is small enough, we need to have the last two items work this way)
      const size = listItems.length;
      const lastItemBuffer = layout === 'compact' ? 2 : 1;

      // if there are only 1-2 items, we don't show the menu "up" because it will hide
      // under the library header. Libraries will need to have a min-heigh to deal with
      // 1-2 item situations
      if (size <= lastItemBuffer) {
        return false;
      }
      return enableUpwardMenus && index >= Math.max(size - lastItemBuffer, 0);
    },
    [enableUpwardMenus, listItems, layout],
  );

  return (
    <div className={className} role="list">
      {listItems.map((listItem, index) => (
        <div className="list-item" key={`${listItem.video.id}`} role="listitem">
          <ListItem
            isRebrand={isRebrand}
            containerSize={containerSize}
            disabled={!enableMultiSelect && selection !== '' && selection !== listItem.video.id}
            enableCopyMenuItems={enableCopyMenuItems}
            enableDeleteMenuItem={enableDeleteMenuItem}
            enableSendWithTemplate={enableSendWithTemplate}
            enableUpwardMenus
            enableVideoEdit={enableVideoEdit}
            handleDropDown={(value) => handleDropDown(listItem.video.id, value)}
            layout={layout}
            // last menu item should open up (used in cases like the end of the list)
            menuDirection={shouldMenuOpenUp(index) ? 'up' : 'down'}
            onMenuOpen={onMenuOpen}
            onSelect={() => handleListItemSelect(listItem.video.id)}
            selected={selection === listItem.video.id || selection.includes(listItem.video.id)}
            shouldShowMenu={shouldShowMenu}
            subtitle={listItem.subtitle}
            team={listItem.team}
            title={listItem.title}
            video={listItem.video}
            videoTranscription={videoTranscription}
            showReviewStatus={showReviewStatus}
          />
        </div>
      ))}
    </div>
  );
};

ListContainer.propTypes = {
  /** Emotion base style */
  className: PropTypes.string.isRequired,
  /** callback for the dropdown menu */
  handleDropDown: PropTypes.func.isRequired,
  /** callback for when a list item is (de) selected */
  onListItemSelect: PropTypes.func.isRequired,
  /** List of items to display */
  listItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      team: PropTypes.string,
      video: PropTypes.shape({
        status: PropTypes.oneOf([
          'UPLOADING',
          'UPLOAD_FAILED',
          'ENCODING',
          'ENCODING_FAILED',
          'PROCESSING',
          'READY',
        ]).isRequired,
        progress: PropTypes.number,
        thumbnail: PropTypes.string,
        id: PropTypes.string.isRequired,
        ctaLink: PropTypes.string,
        ctaTitle: PropTypes.string,
      }),
    }),
  ).isRequired,
  /** selected list item */
  selection: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
    .isRequired,
  /** should show list item menu */
  shouldShowMenu: PropTypes.bool,
  /** enable copy menu items */
  enableCopyMenuItems: PropTypes.bool,
  /** enable delete menu item */
  enableDeleteMenuItem: PropTypes.bool,
  /** Layout style choice */
  layout: PropTypes.oneOf(['default', 'compact']),
  /** size indication from parent components */
  containerSize: PropTypes.oneOf(['', 'w360', 'chrome', 'small', 'xsmall', 'mobile']),
  /** enable send with template menu item. */
  enableSendWithTemplate: PropTypes.bool,
  /** enables the Edit Video menu item */
  enableVideoEdit: PropTypes.bool,
  /** called when a list item menu is opened */
  onMenuOpen: PropTypes.func,
  /** allow menus to open up, not just down */
  enableUpwardMenus: PropTypes.bool,
  /** api callback */
  videoTranscription: PropTypes.string,
  /** Use new branding */
  isRebrand: PropTypes.bool,
  /** Enable multi select */
  enableMultiSelect: PropTypes.bool,
  /** Show video review status indicator */
  showReviewStatus: PropTypes.bool,
};

ListContainer.defaultProps = {
  containerSize: '',
  enableCopyMenuItems: false,
  enableDeleteMenuItem: false,
  enableSendWithTemplate: false,
  enableUpwardMenus: false,
  enableVideoEdit: true,
  layout: 'default',
  shouldShowMenu: true,
  onMenuOpen: () => {},
  videoTranscription: null,
  isRebrand: false,
  enableMultiSelect: false,
  showReviewStatus: false,
};

const styledListContainer = styled(ListContainer)`
  height: calc(100% - 9.375rem);
  overflow-y: auto;

  .list-item {
    border-bottom: 0.062rem solid ${(props) => (props.isRebrand ? colors.mist : colors.whiteSmoke)};
  }
`;

export default styledListContainer;
